import React, { useState } from "react";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Switch, TextField } from "@material-ui/core";
import { SupportPriority, useSupportPriorities } from "./hook/useSupportPriorities";
import {
	ActionRow,
	ActiveDot,
	CodeCell,
	ColorSwatch,
	EmptyRow,
	ErrorAlert,
	PageWrap,
	SectionCard,
	SectionHeader,
	SectionTitle,
	StyledTable,
	TableWrap,
} from "../SupportCategories/styled/SupportCategoriesStyled";
import PermissionGate from "../../controls/auth/PermissionGate";

const BLANK: Omit<SupportPriority, "id"> = {
	code: "",
	label: "",
	color: null,
	sortOrder: 0,
	isActive: true,
};

export default function SupportPrioritiesPage({ embedded }: { embedded?: boolean }) {
	const { priorities, loading, error, create, update, remove } = useSupportPriorities();
	const [dialogOpen, setDialogOpen] = useState(false);
	const [editing, setEditing] = useState<SupportPriority | null>(null);
	const [form, setForm] = useState<Omit<SupportPriority, "id">>(BLANK);
	const [saving, setSaving] = useState(false);
	const [formError, setFormError] = useState("");
	const [deleteTarget, setDeleteTarget] = useState<SupportPriority | null>(null);
	const [deleteForce, setDeleteForce] = useState(false);
	const [deleteError, setDeleteError] = useState("");
	const [deleting, setDeleting] = useState(false);

	function openCreate() {
		setEditing(null);
		setForm({ ...BLANK });
		setFormError("");
		setDialogOpen(true);
	}

	function openEdit(priority: SupportPriority) {
		setEditing(priority);
		setForm({
			code: priority.code,
			label: priority.label,
			color: priority.color,
			sortOrder: priority.sortOrder,
			isActive: priority.isActive,
		});
		setFormError("");
		setDialogOpen(true);
	}

	async function handleSave() {
		setFormError("");
		setSaving(true);
		try {
			if (editing) {
				await update(editing.id, {
					label: form.label,
					color: form.color,
					sortOrder: form.sortOrder,
					isActive: form.isActive,
				});
			} else {
				await create(form);
			}
			setDialogOpen(false);
		} catch (e: any) {
			setFormError(e?.message || "Save failed");
		} finally {
			setSaving(false);
		}
	}

	async function handleToggleActive(priority: SupportPriority) {
		await update(priority.id, { isActive: !priority.isActive });
	}

	async function handleDelete() {
		if (!deleteTarget) return;
		setDeleting(true);
		setDeleteError("");
		try {
			await remove(deleteTarget.id, deleteForce);
			setDeleteTarget(null);
			setDeleteForce(false);
		} catch (e: any) {
			const msg = e?.response?.data?.message || e?.message || "Delete failed";
			if (e?.response?.status === 409) {
				setDeleteError(msg);
				setDeleteForce(true);
			} else {
				setDeleteError(msg);
			}
		} finally {
			setDeleting(false);
		}
	}

	const content = (
		<>
			{error && <ErrorAlert>{error}</ErrorAlert>}

			<SectionCard>
				<SectionHeader>
					<SectionTitle>Priority levels</SectionTitle>
					<PermissionGate permission="tickets.assign">
						<Button
							variant="contained"
							color="primary"
							size="small"
							onClick={openCreate}
							disabled={loading}
							style={{ borderRadius: 8, textTransform: "none", fontWeight: 600, fontSize: "0.82rem" }}>
							+ New Priority
						</Button>
					</PermissionGate>
				</SectionHeader>

				<TableWrap>
					<StyledTable>
						<thead>
							<tr>
								<th>Label</th>
								<th>Code</th>
								<th>Color</th>
								<th style={{ width: 60 }}>Order</th>
								<th style={{ width: 60 }}>Active</th>
								<th style={{ width: 100, textAlign: "right" }}>Actions</th>
							</tr>
						</thead>
						<tbody>
							{priorities.map((p) => (
								<tr key={p.id}>
									<td style={{ fontWeight: 600 }}>
										<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
											{p.color && (
												<span
													style={{
														display: "inline-block",
														width: 10,
														height: 10,
														borderRadius: "50%",
														background: p.color,
														flexShrink: 0,
													}}
												/>
											)}
											{p.label}
										</div>
									</td>
									<td>
										<CodeCell>{p.code}</CodeCell>
									</td>
									<td>
										{p.color ? (
											<ColorSwatch hex={p.color}>{p.color}</ColorSwatch>
										) : (
											<span
												style={{ color: "#94a3b8", fontSize: "0.82rem", fontStyle: "italic" }}>
												none
											</span>
										)}
									</td>
									<td>{p.sortOrder}</td>
									<td>
										<ActiveDot on={p.isActive} />
									</td>
									<td style={{ textAlign: "right" }}>
										<PermissionGate permission="tickets.assign">
											<Button
												size="small"
												onClick={() => openEdit(p)}
												style={{ minWidth: 0, textTransform: "none", fontSize: "0.8rem" }}>
												Edit
											</Button>
											<Button
												size="small"
												style={{
													minWidth: 0,
													color: "#dc2626",
													textTransform: "none",
													fontSize: "0.8rem",
												}}
												onClick={() => {
													setDeleteTarget(p);
													setDeleteError("");
													setDeleteForce(false);
												}}>
												Delete
											</Button>
										</PermissionGate>
									</td>
								</tr>
							))}
							{!loading && priorities.length === 0 && (
								<tr>
									<EmptyRow
										colSpan={6}
										as="td">
										No priority levels configured yet.
									</EmptyRow>
								</tr>
							)}
						</tbody>
					</StyledTable>
				</TableWrap>
			</SectionCard>

			{/* Create / Edit dialog */}
			<Dialog
				open={dialogOpen}
				onClose={() => setDialogOpen(false)}
				maxWidth="sm"
				fullWidth>
				<DialogTitle style={{ fontWeight: 600 }}>{editing ? "Edit Priority" : "New Priority"}</DialogTitle>
				<DialogContent style={{ display: "flex", flexDirection: "column", gap: 16, paddingTop: 8 }}>
					{formError && <ErrorAlert style={{ margin: 0 }}>{formError}</ErrorAlert>}
					{!editing && (
						<TextField
							label="Code (slug)"
							value={form.code}
							onChange={(e) => {
								const slug = e.target.value
									.toLowerCase()
									.replace(/[^a-z0-9_]/g, "_")
									.replace(/_+/g, "_")
									.slice(0, 32);
								setForm((f) => ({ ...f, code: slug }));
							}}
							helperText="Lowercase letters, numbers, underscores."
							size="small"
							variant="outlined"
						/>
					)}
					<TextField
						label="Label"
						value={form.label}
						onChange={(e) => {
							const label = e.target.value;
							setForm((f) => {
								const updates: Partial<Omit<SupportPriority, "id">> = { label };
								if (!editing && !f.code) {
									updates.code = label
										.toLowerCase()
										.replace(/[^a-z0-9_]/g, "_")
										.replace(/_+/g, "_")
										.slice(0, 32);
								}
								return { ...f, ...updates };
							});
						}}
						required
						size="small"
						variant="outlined"
					/>
					<div style={{ display: "flex", alignItems: "center", gap: 10 }}>
						<input
							type="color"
							value={form.color ?? "#2196f3"}
							onChange={(e) => setForm((f) => ({ ...f, color: e.target.value }))}
							style={{
								width: 40,
								height: 40,
								padding: 2,
								border: "1px solid #e2e8f0",
								borderRadius: 8,
								cursor: "pointer",
								flexShrink: 0,
							}}
						/>
						<TextField
							label="Color"
							value={form.color ?? ""}
							onChange={(e) => setForm((f) => ({ ...f, color: e.target.value || null }))}
							placeholder="#ff9800"
							size="small"
							variant="outlined"
							style={{ flex: 1 }}
						/>
					</div>
					<TextField
						label="Sort Order"
						type="number"
						value={form.sortOrder}
						onChange={(e) => setForm((f) => ({ ...f, sortOrder: parseInt(e.target.value, 10) || 0 }))}
						size="small"
						variant="outlined"
					/>
					<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
						<Switch
							checked={form.isActive}
							color="primary"
							onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
						/>
						<span style={{ fontSize: "0.9rem" }}>Active</span>
					</div>
				</DialogContent>
				<DialogActions style={{ padding: "12px 24px" }}>
					<Button
						onClick={() => setDialogOpen(false)}
						disabled={saving}>
						Cancel
					</Button>
					<Button
						onClick={handleSave}
						color="primary"
						variant="contained"
						disabled={saving || !form.label}
						style={{ borderRadius: 8, textTransform: "none", fontWeight: 600 }}>
						{saving ? "Saving..." : "Save"}
					</Button>
				</DialogActions>
			</Dialog>

			{/* Delete confirm dialog */}
			<Dialog
				open={!!deleteTarget}
				onClose={() => {
					setDeleteTarget(null);
					setDeleteForce(false);
				}}>
				<DialogTitle style={{ fontWeight: 600 }}>Delete "{deleteTarget?.label}"?</DialogTitle>
				<DialogContent>
					{deleteError ? (
						<div style={{ color: "#dc2626", fontSize: "0.9rem", lineHeight: 1.6 }}>
							{deleteError}
							<br />
							Click Delete again to force-delete.
						</div>
					) : (
						<div style={{ color: "#475569", lineHeight: 1.6 }}>
							This will permanently remove the priority level. Existing tickets keep their priority code.
						</div>
					)}
				</DialogContent>
				<DialogActions style={{ padding: "12px 24px" }}>
					<Button
						onClick={() => {
							setDeleteTarget(null);
							setDeleteForce(false);
						}}
						disabled={deleting}>
						Cancel
					</Button>
					<Button
						onClick={handleDelete}
						style={{
							color: "#fff",
							background: "#dc2626",
							borderRadius: 8,
							textTransform: "none",
							fontWeight: 600,
						}}
						variant="contained"
						disabled={deleting}>
						{deleting ? "Deleting..." : deleteForce ? "Force Delete" : "Delete"}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);

	return embedded ? content : <PageWrap>{content}</PageWrap>;
}
